// src/pages/AlarmDetails/tabs/OverviewTab.jsx
// Overview tab showing alarm state, countdown timer, and quick actions

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Timer as TimerIcon,
  Person as PersonIcon,
  DeviceHub as DeviceIcon,
  LocationOn as LocationIcon,
  NotificationsActive as NotificationIcon,
  CheckCircle as CheckCircleIcon,
  LocalHospital as EmergencyIcon,
  Cancel as CancelIcon,
  AccessTime as TimeIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { useIsMobile } from '../../../hooks/useResponsive';

const OverviewTab = ({ alarm, onUpdateStatus, onRefresh }) => {
  const isMobile = useIsMobile();
  const [countdown, setCountdown] = useState(null);
  const [countdownProgress, setCountdownProgress] = useState(100);

  // Comment dialog state for terminal status changes
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');

  const stateCode = alarm?.alarmStateModel?.code;
  const isCountdown = stateCode === 'countdown';
  const isDispatched = stateCode === 'dispatched';
  const isActive = !alarm?.alarmStateModel?.isTerminal;

  // Resolve person information from multiple sources
  const getPersonInfo = () => {
    // Check all possible sources for person data
    const person = alarm?.personModel || alarm?.deviceModel?.personModel;
    const personName = person?.friendlyName || alarm?.Content?.personName;
    const personId = person?.id ||
                     alarm?.Content?.personId ||
                     alarm?.deviceModel?.devicePerson ||
                     alarm?.buttonDeviceModel?.devicePerson;
    const personLocation = person?.locationModel ||
                           (alarm?.Content?.personLocationId ? {
                             id: alarm.Content.personLocationId,
                             friendlyName: alarm.Content.personLocationName
                           } : null);

    return { person, personName, personId, personLocation };
  };

  // Resolve location information with mobile button logic
  const getLocationInfo = () => {
    const antennas = alarm?.Content?.antennas || [];
    const { personLocation } = getPersonInfo();
    const personLocationId = personLocation?.id || alarm?.Content?.personLocationId;

    // Determine if this is a mobile button
    const isMobileButton = alarm?.Content?.buttonType === 'mobile' ||
                           alarm?.Content?.buttonType === 'emergencyMobile' ||
                           alarm?.deviceModel?.devicePerson ||
                           alarm?.buttonDeviceModel?.devicePerson ||
                           alarm?.personModel?.id ||
                           alarm?.Content?.personId;

    // For mobile buttons, check if person's home location is in antenna signals
    if (isMobileButton && personLocationId && antennas.length > 0) {
      const personLocationInAntennas = antennas.find(ant =>
        Number(ant.locationId) === Number(personLocationId) ||
        String(ant.locationId) === String(personLocationId)
      );

      if (personLocationInAntennas) {
        // Person's home location confirmed by antenna signal
        return {
          name: personLocation?.friendlyName || personLocationInAntennas.locationName || alarm?.locationModel?.friendlyName,
          id: personLocationId,
          source: 'home_confirmed',
          sourceLabel: "Person's home location (confirmed by signal)",
          antenna: personLocationInAntennas
        };
      } else {
        // Person has home location but they're not there - use strongest signal
        const strongestAntenna = antennas.reduce((best, ant) =>
          (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null);

        if (strongestAntenna) {
          return {
            name: strongestAntenna.locationName || alarm?.locationModel?.friendlyName,
            id: strongestAntenna.locationId,
            source: 'signal',
            sourceLabel: 'Strongest antenna signal (not at home location)',
            antenna: strongestAntenna,
            personHomeLocation: personLocation?.friendlyName
          };
        }
      }
    }

    // For mobile buttons without home location, use strongest signal
    if (isMobileButton && antennas.length > 0) {
      const strongestAntenna = antennas.reduce((best, ant) =>
        (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null);

      if (strongestAntenna) {
        return {
          name: strongestAntenna.locationName || alarm?.locationModel?.friendlyName,
          id: strongestAntenna.locationId,
          source: 'signal',
          sourceLabel: 'From antenna signal',
          antenna: strongestAntenna
        };
      }
    }

    // Direct location model (highest priority for fixed buttons)
    if (alarm?.locationModel?.friendlyName) {
      return {
        name: alarm.locationModel.friendlyName,
        id: alarm.locationModel.id,
        source: 'direct',
        sourceLabel: alarm.locationModel.locationTypeModel?.friendlyName || 'Direct assignment'
      };
    }

    // Person's assigned location (for mobile buttons without antenna data)
    if (personLocation?.friendlyName) {
      return {
        name: personLocation.friendlyName,
        id: personLocation.id,
        source: 'person_home',
        sourceLabel: "Person's assigned location"
      };
    }

    // Device location (fixed buttons)
    if (alarm?.deviceModel?.locationModel?.friendlyName) {
      return {
        name: alarm.deviceModel.locationModel.friendlyName,
        id: alarm.deviceModel.locationModel.id,
        source: 'device',
        sourceLabel: 'Device location (fixed button)'
      };
    }

    // Fallback to antenna data with location name
    if (antennas.length > 0) {
      const strongestAntenna = antennas.reduce((best, ant) =>
        (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null);

      if (strongestAntenna?.locationName) {
        return {
          name: strongestAntenna.locationName,
          id: strongestAntenna.locationId,
          source: 'signal',
          sourceLabel: 'From antenna signal',
          antenna: strongestAntenna
        };
      }
    }

    return {
      name: 'Unknown location',
      id: null,
      source: 'unknown',
      sourceLabel: 'Location could not be determined'
    };
  };

  const personInfo = getPersonInfo();
  const locationInfo = getLocationInfo();

  // Get countdown duration from person/location settings (AlarmCountdownTime) or alarm type
  const getCountdownSeconds = () => {
    // Helper to check array-style custom fields
    const getCustomFieldValue = (data, fieldNames) => {
      if (!data || !Array.isArray(data)) return null;
      for (const fieldName of fieldNames) {
        const field = data.find(f => f.name === fieldName || f.fieldName === fieldName);
        if (field?.value) {
          const parsed = parseInt(field.value, 10);
          if (!isNaN(parsed) && parsed > 0) return { value: parsed, source: fieldName };
        }
      }
      return null;
    };

    // Check person's custom fields first
    const personCustom = getCustomFieldValue(alarm?.personModel?.data, ['AlarmCountdownTime', 'Alarm_Countdown_Time', 'alarmCountdownSeconds']);
    if (personCustom) return { seconds: personCustom.value, source: 'person' };

    // Check location's custom fields
    const locationCustom = getCustomFieldValue(alarm?.locationModel?.data, ['AlarmCountdownTime', 'Alarm_Countdown_Time', 'alarmCountdownSeconds']);
    if (locationCustom) return { seconds: locationCustom.value, source: 'location' };

    // Fallback to alarm type setting, then default 60 seconds
    if (alarm?.alarmTypeModel?.countdownSeconds) {
      return { seconds: alarm.alarmTypeModel.countdownSeconds, source: 'alarmType' };
    }
    return { seconds: 60, source: 'default' };
  };
  const countdownInfo = getCountdownSeconds();
  const countdownSeconds = countdownInfo.seconds;

  // Handle status change - show dialog for terminal states
  const handleStatusChange = (status) => {
    const terminalStates = ['finished', 'cancelled'];
    if (terminalStates.includes(status)) {
      setPendingStatus(status);
      setComment('');
      setCommentError('');
      setCommentDialogOpen(true);
    } else {
      onUpdateStatus(status);
    }
  };

  // Submit status change with comment
  const handleCommentSubmit = () => {
    if (!comment.trim()) {
      setCommentError('Please provide a comment explaining why the alarm is being ' +
        (pendingStatus === 'cancelled' ? 'cancelled' : 'resolved'));
      return;
    }
    onUpdateStatus(pendingStatus, comment.trim());
    setCommentDialogOpen(false);
    setPendingStatus(null);
    setComment('');
  };

  // Close dialog without submitting
  const handleDialogClose = () => {
    setCommentDialogOpen(false);
    setPendingStatus(null);
    setComment('');
    setCommentError('');
  };

  // Calculate and update countdown
  useEffect(() => {
    if (!isCountdown || !alarm?.firstPressAt) return;

    const calculateCountdown = () => {
      const firstPress = new Date(alarm.firstPressAt);
      const endTime = new Date(firstPress.getTime() + countdownSeconds * 1000);
      const now = new Date();
      const remaining = Math.max(0, differenceInSeconds(endTime, now));

      setCountdown(remaining);
      setCountdownProgress((remaining / countdownSeconds) * 100);

      if (remaining <= 0) {
        // Countdown finished, refresh to get updated state
        onRefresh?.();
      }
    };

    calculateCountdown();
    const timer = setInterval(calculateCountdown, 1000);

    return () => clearInterval(timer);
  }, [isCountdown, alarm?.firstPressAt, countdownSeconds, onRefresh]);

  // Format countdown display
  const formatCountdown = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get countdown urgency color
  const getCountdownColor = () => {
    if (countdown === null) return 'primary';
    if (countdown <= 10) return 'error';
    if (countdown <= 20) return 'warning';
    return 'success';
  };

  return (
    <Box>
      {/* Countdown Banner (if in countdown state) */}
      {isCountdown && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            textAlign: 'center',
            backgroundColor: countdown <= 10 ? 'error.dark' : countdown <= 20 ? 'warning.dark' : 'primary.dark',
            color: 'white'
          }}
        >
          <Typography variant="overline" sx={{ opacity: 0.9 }}>
            Time Until Emergency Services Notified
          </Typography>
          <Typography
            variant="h1"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 'bold',
              fontSize: { xs: '4rem', md: '6rem' },
              my: 2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            {formatCountdown(countdown)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={countdownProgress}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white',
                borderRadius: 5
              }
            }}
          />
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Press cancel button to stop alarm or wait for automatic escalation
          </Typography>
        </Paper>
      )}

      {/* Status Update Actions */}
      {isActive && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmergencyIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
            Quick Actions
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap'
          }}>
            {/* After dispatched, only show Resolve button */}
            {isDispatched ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleStatusChange('finished')}
              >
                Resolve Alarm
              </Button>
            ) : (
              <>
                {/* Acknowledge button - not shown if already acknowledged or dispatched */}
                {stateCode !== 'acknowledged' && stateCode !== 'dispatched' && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleStatusChange('acknowledged')}
                  >
                    Acknowledge
                  </Button>
                )}
                {/* Dispatched button - not shown if already dispatched */}
                {stateCode !== 'dispatched' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EmergencyIcon />}
                    onClick={() => handleStatusChange('dispatched')}
                  >
                    Mark Dispatched
                  </Button>
                )}
                {/* Resolve button */}
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleStatusChange('finished')}
                >
                  Resolve Alarm
                </Button>
                {/* Cancel button - only show during countdown phase AND while countdown is still active */}
                {isCountdown && countdown !== null && countdown > 0 && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CancelIcon />}
                    onClick={() => handleStatusChange('cancelled')}
                  >
                    Cancel Alarm
                  </Button>
                )}
              </>
            )}
          </Box>
        </Paper>
      )}

      {/* Comment Dialog for Terminal States */}
      <Dialog open={commentDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pendingStatus === 'cancelled' ? 'Cancel Alarm' : 'Resolve Alarm'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pendingStatus === 'cancelled'
              ? 'Please provide a reason for cancelling this alarm (e.g., false alarm, test, accidental trigger).'
              : 'Please provide details about how this alarm was resolved (e.g., emergency services responded, person assisted, situation resolved).'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Comment *"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (commentError) setCommentError('');
            }}
            error={!!commentError}
            helperText={commentError || 'This comment will be recorded in the alarm history'}
            placeholder={pendingStatus === 'cancelled'
              ? 'e.g., False alarm - button pressed accidentally'
              : 'e.g., Emergency services arrived, person is safe'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleCommentSubmit}
            variant="contained"
            color={pendingStatus === 'cancelled' ? 'warning' : 'success'}
          >
            {pendingStatus === 'cancelled' ? 'Cancel Alarm' : 'Resolve Alarm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {/* Alarm Info Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="primary" />
                Alarm Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Type</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {alarm?.alarmTypeModel?.friendlyName || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Priority</Typography>
                  <Chip
                    label={`Priority ${alarm?.alarmTypeModel?.priority || 1}`}
                    color={alarm?.alarmTypeModel?.priority === 1 ? 'error' : 'warning'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Countdown Duration</Typography>
                  <Typography variant="body1">
                    {countdownSeconds} seconds
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (from {countdownInfo.source === 'person' ? 'person settings' :
                           countdownInfo.source === 'location' ? 'location settings' :
                           countdownInfo.source === 'alarmType' ? 'alarm type' : 'default'})
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Notifications Sent</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationIcon fontSize="small" color={alarm?.notificationCount > 0 ? 'success' : (!isCountdown ? 'warning' : 'action')} />
                    {alarm?.notificationCount || 0}
                  </Typography>
                  {!isCountdown && (!alarm?.notificationCount || alarm?.notificationCount === 0) && (
                    <Typography variant="caption" color="warning.main">
                      No notifications sent yet
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Device & Person Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeviceIcon color="primary" />
                Device & Person
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Button Device</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {alarm?.buttonDeviceModel?.friendlyName || alarm?.Content?.buttonDeviceName || alarm?.Content?.deviceName || 'Unknown Device'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    HEX: {alarm?.Content?.deviceKey || alarm?.buttonDeviceModel?.config?.identity?.HEX || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Associated Person</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body1">
                      {personInfo.personName || (personInfo.personId ? `Person ID: ${personInfo.personId}` : 'No person associated')}
                    </Typography>
                  </Box>
                  {/* Show person's assigned location (home location) */}
                  {personInfo.personLocation?.friendlyName && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <LocationIcon fontSize="inherit" />
                      Home location: {personInfo.personLocation.friendlyName}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Alarm Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color={locationInfo.source === 'home_confirmed' ? 'success' : 'action'} />
                    <Typography variant="body1" fontWeight="medium">
                      {locationInfo.name}
                    </Typography>
                  </Box>
                  {/* Show location source with appropriate styling */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: locationInfo.source === 'home_confirmed' ? 'success.main' :
                             locationInfo.source === 'signal' ? 'warning.main' :
                             'text.secondary'
                    }}
                  >
                    {locationInfo.sourceLabel}
                  </Typography>
                  {/* Show antenna signal strength if available */}
                  {locationInfo.antenna && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Signal: {locationInfo.antenna.rssiDbm} dBm ({locationInfo.antenna.signalQuality || 'unknown'})
                    </Typography>
                  )}
                  {/* If person is not at home location, show where their home is */}
                  {locationInfo.personHomeLocation && locationInfo.source === 'signal' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      label={`Home: ${locationInfo.personHomeLocation}`}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Timeline Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon color="primary" fontSize={isMobile ? 'small' : 'medium'} />
                Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">First Press</Typography>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="medium">
                      {alarm?.firstPressAt
                        ? format(new Date(alarm.firstPressAt), 'HH:mm:ss')
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {alarm?.firstPressAt
                        ? format(new Date(alarm.firstPressAt), 'MMM d, yyyy')
                        : ''}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">{isMobile ? 'Emergency' : 'Sent to Emergency'}</Typography>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="medium">
                      {alarm?.sentToEmergencyAt
                        ? format(new Date(alarm.sentToEmergencyAt), 'HH:mm:ss')
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {alarm?.sentToEmergencyAt
                        ? formatDistanceToNow(new Date(alarm.sentToEmergencyAt), { addSuffix: true })
                        : 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">{isMobile ? 'Ack.' : 'Acknowledged'}</Typography>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="medium">
                      {alarm?.acknowledgedAt
                        ? format(new Date(alarm.acknowledgedAt), 'HH:mm:ss')
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {alarm?.acknowledgedAt
                        ? formatDistanceToNow(new Date(alarm.acknowledgedAt), { addSuffix: true })
                        : 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">Dispatched</Typography>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="medium">
                      {alarm?.dispatchedAt
                        ? format(new Date(alarm.dispatchedAt), 'HH:mm:ss')
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {alarm?.dispatchedAt
                        ? formatDistanceToNow(new Date(alarm.dispatchedAt), { addSuffix: true })
                        : 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">Finished</Typography>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="medium">
                      {alarm?.finishedAt
                        ? format(new Date(alarm.finishedAt), 'HH:mm:ss')
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {alarm?.finishedAt
                        ? formatDistanceToNow(new Date(alarm.finishedAt), { addSuffix: true })
                        : 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Location Reasoning Card */}
        {alarm?.locationReasoning && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              icon={<LocationIcon />}
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Typography variant="subtitle2" gutterBottom>Location Determination</Typography>
              <Typography variant="body2">{alarm.locationReasoning}</Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default OverviewTab;
